import { useState } from 'react';
// @mui
import {
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
} from '@mui/material';
import { StepIconProps } from '@mui/material/StepIcon';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { organizationApi } from 'src/api/organization';
import { userApi } from 'src/api/user';
import { useUserContext } from 'src/auth/useUserContext';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { DARK_TEXT } from 'src/theme/palette';
import {
  GetUserDto,
  GetUserDtoRegistrationSourceEnum,
  UpdateOrganizationDto,
  UpdateUserDto,
} from 'woofwoof-api';
import Iconify from '../../../../components/iconify';
import { useOrganizationContext } from '../../organization/hooks/useOrganizationContext';
import AccountBusiness, {
  AccountBusinessFormValuesProps,
} from './AccountBusiness';
import { AccountGeneralFormValuesProps } from './AccountGeneral';
import { AccountGeneral } from './index';
import UpdateAccountInvitationStep from './UpdateAccountInvitationStep';
import { translateKey } from 'src/locales/translate';

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 23,
    left: 'calc(-50% + 24px)',
    right: 'calc(50% + 24px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.text.primary,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.text.primary,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 4,
    border: 0,
    borderRadius: 1,
    backgroundColor: theme.palette.divider,
  },
}));

const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  zIndex: 1,
  width: 48,
  height: 48,
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.action.disabledBackground,
  ...(ownerState.active && {
    color: DARK_TEXT.primary,
    backgroundColor: theme.palette.text.primary,
  }),
  ...(ownerState.completed && {
    color: DARK_TEXT.primary,
    backgroundColor: theme.palette.text.primary,
  }),
}));

function ColorlibStepIcon(props: StepIconProps & { isInvitedUser?: boolean }) {
  const { active, completed, className, icon, isInvitedUser } = props;

  const icons: { [index: string]: React.ReactElement } = {
    1: <Iconify icon="ic:person" width={24} />,
    2: (
      <Iconify
        icon={
          isInvitedUser
            ? 'ic:baseline-group-add'
            : 'ic:baseline-medical-services'
        }
        width={24}
      />
    ),
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {icons[String(icon)]}
    </ColorlibStepIconRoot>
  );
}

interface Props {
  initialStep?: number;
}

export default function UpdateAccountStepper({ initialStep = 0 }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { data, refetch } = useUserContext();
  const { refetch: refetchOrganization } = useOrganizationContext();
  // Get the user's primary organization (first organization they belong to)
  const primaryOrganizationId = data?.organizations?.[0]?.organization;

  const isInvitedUser =
    data?.registrationSource === GetUserDtoRegistrationSourceEnum.Invitation;

  // Invited newcomers decide on their invitation instead of describing a clinic.
  const steps = [
    translateKey('hardcoded.szczegoly_konta'),
    isInvitedUser
      ? translateKey('invitationAcceptance.stepLabel')
      : translateKey('hardcoded.placowka'),
  ];

  const { mutateAsync: updateUser, isLoading: isUpdatingUser } = useMutation({
    mutationFn: (updateUserDto: UpdateUserDto) =>
      userApi.userControllerUpdateUser({ updateUserDto }),
  });

  const { mutateAsync, isLoading: isUpdatingOrganization } = useMutation({
    mutationFn: ({
      organizationId,
      updateData,
    }: {
      organizationId: string;
      updateData: UpdateOrganizationDto;
    }) =>
      organizationApi.organizationControllerUpdateOrganization({
        id: organizationId,
        updateOrganizationDto: updateData,
      }),
  });

  const isLoading = isUpdatingUser || isUpdatingOrganization;
  const [stepsFormValues, setStepsFormValues] = useState<
    [
      AccountGeneralFormValuesProps | GetUserDto | undefined,
      AccountBusinessFormValuesProps | GetUserDto | undefined,
    ]
  >([data, undefined]);
  const [activeStep, setActiveStep] = useState(initialStep);

  const handleNext = async (stepFormValues: AccountGeneralFormValuesProps) => {
    if (isInvitedUser) {
      // Onboarding completes with the invitation decision in step 2, so the
      // AuthGuard keeps the newcomer here until then.
      try {
        await updateUser({
          ...stepFormValues,
          avatar: stepFormValues.avatar || undefined,
        });
        // Saving already marks the account onboarded on the server. The cached
        // user stays as it was until the decision, so the AuthGuard keeps the
        // newcomer in the wizard; step 2 has its own route for the same reason.
        navigate(PATH_DASHBOARD.general.updateAccountInvitation, {
          replace: true,
        });
      } catch (error) {
        enqueueSnackbar(error.message || error, { variant: 'error' });
      }
    } else {
      // Self-registered - proceed to Step 2
      setStepsFormValues((_steps) => [stepFormValues, _steps[1]]);
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = (stepFormValues: AccountBusinessFormValuesProps) => {
    setStepsFormValues((_steps) => [_steps[0], stepFormValues]);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async (
    stepFormValues: AccountBusinessFormValuesProps,
  ) => {
    try {
      if (stepsFormValues[0]) {
        const userFormData = stepsFormValues[0];
        const organizationFormData = stepFormValues;

        if (!primaryOrganizationId) {
          throw new Error(
            translateKey('hardcoded.nie_znaleziono_organizacji_uzytkownika'),
          );
        }

        await mutateAsync({
          organizationId: primaryOrganizationId,
          updateData: {
            ...organizationFormData,
            logoUrl:
              organizationFormData.logoUrl &&
              typeof organizationFormData.logoUrl === 'string'
                ? organizationFormData.logoUrl
                : undefined,
          },
        });

        await updateUser({
          ...userFormData,
          avatar: userFormData.avatar || undefined,
        });

        await refetch();
        await refetchOrganization();
      }
    } catch (error) {
      enqueueSnackbar(error.message || error, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      {steps.length > 1 && (
        <Stepper
          alternativeLabel
          activeStep={activeStep}
          connector={<ColorlibConnector />}
          sx={{ marginBottom: 5 }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={(iconProps: StepIconProps) => (
                  <ColorlibStepIcon
                    {...iconProps}
                    isInvitedUser={isInvitedUser}
                  />
                )}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      )}
      {activeStep === 0 && (
        <AccountGeneral
          submitButtonLabel={translateKey('quickBooking.next')}
          stepValues={stepsFormValues[0]}
          onSubmit={handleNext}
          isStepper={!isInvitedUser}
        />
      )}
      {activeStep === 1 && isInvitedUser && <UpdateAccountInvitationStep />}
      {activeStep === 1 && !isInvitedUser && (
        <AccountBusiness
          onSubmit={handleSubmit}
          onBack={handleBack}
          stepValues={stepsFormValues[1]}
          isLoading={isLoading || isUpdatingOrganization}
        />
      )}
    </>
  );
}
